import { getRequest, getRawResponse, postRequest, patchRequest, deleteRequest, postFormData } from './http.service';
import {
  URL_UPLOAD,
  URL_DRIVE_CREATE,
  URL_DRIVE_ROOT,
  URL_DRIVE_ORG_TRUE_ROOT,
  URL_DRIVE_BY_ID,
  URL_DRIVE_ITEM_RENAME,
  URL_DRIVE_ITEM_MOVE,
  URL_DRIVE_CLIENT_RECENT,
  URL_DRIVE_ITEM_CONTENT,
  URL_DRIVE_ITEM_FILE,
  URL_DRIVE_ITEM_DOWNLOAD_ZIP,
  URL_DRIVE_CLIENT_FOLDERS,
  URL_DRIVE_ROOT_FILTERED,
  URL_DRIVE_FOLDER_FILTERED,
  URL_CUSTOMERS_IMAGES_META,
} from './url.service';

export const fetchFolderContents = (folderId: string, clientId: string) =>
  getRequest(`${URL_DRIVE_BY_ID(folderId)}?clientId=${clientId}`);

export const createFolder = (parentId: string | undefined, name: string, clientId?: string) =>
  postRequest(URL_DRIVE_CREATE, { parentId, name, clientId });

export const uploadFiles = (formData: FormData) =>
  postFormData(URL_UPLOAD, formData);

export const renameItem = (itemId: string, newName: string) =>
  patchRequest(URL_DRIVE_ITEM_RENAME(itemId), { newName });

export const moveItem = (itemId: string, newParentId: string) =>
  patchRequest(URL_DRIVE_ITEM_MOVE(itemId), { newParentId });

export const deleteItem = (itemId: string) =>
  deleteRequest(URL_DRIVE_BY_ID(itemId));

export const fetchRecentClientFiles = (clientId: string) =>
  getRequest(URL_DRIVE_CLIENT_RECENT(clientId));

export const fetchDriveFolder = (folderId: string | undefined, clientId?: string) => {
  if (folderId) {
    const base = URL_DRIVE_BY_ID(folderId);
    return getRequest(clientId ? `${base}?clientId=${clientId}` : base);
  }
  return clientId
    ? getRequest(`${URL_DRIVE_ROOT}?clientId=${clientId}`)
    : getRequest(URL_DRIVE_ORG_TRUE_ROOT);
};

export const fetchDriveItemContent = (itemId: string) =>
  getRequest(URL_DRIVE_ITEM_CONTENT(itemId));

/**
 * Fetch a drive item's raw bytes. Served `inline` by the backend so the viewer
 * can render the file rather than the browser downloading it.
 */
export const getDriveItemBytes = async (itemId: string): Promise<ArrayBuffer> => {
  const res = await getRawResponse(URL_DRIVE_ITEM_FILE(itemId));
  if (!res.ok) throw new Error("Failed to load the file");
  return res.arrayBuffer();
};

/** Download a folder as a zip — returns the raw Response for blob handling */
export const downloadDriveItemZip = (itemId: string) =>
  getRawResponse(URL_DRIVE_ITEM_DOWNLOAD_ZIP(itemId));

export const fetchClientDriveFolders = (clientId: string) =>
  getRequest(URL_DRIVE_CLIENT_FOLDERS(clientId));

/** Get the drive root, filtered to items relevant to a specific client (e-sign browser) */
export const fetchFilteredDriveRoot = (clientId: string) =>
  getRequest(URL_DRIVE_ROOT_FILTERED(clientId));

/** Get a folder's contents, filtered to items relevant to a specific client */
export const fetchFilteredDriveFolder = (folderId: string, clientId: string) =>
  getRequest(URL_DRIVE_FOLDER_FILTERED(folderId, clientId));

/** Get customer document metadata for a batch of image IDs (core-logic customers) */
export const fetchCustomerImagesMetadata = (imageIdList: unknown[]) =>
  postRequest(URL_CUSTOMERS_IMAGES_META, { imageIdList });
